
import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const PCSContactForm = ({ formId, title, subtitle }: { formId: string; title: string; subtitle: string }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    pcsDate: '',
    currentStatus: '',
    propertyCondition: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('pcsDate', formData.pcsDate);
      formBody.append('currentStatus', formData.currentStatus);
      formBody.append('propertyCondition', formData.propertyCondition);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d678vk5bd17lru29kc70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You for Your Service!</h3>
        <p className="text-gray-600">
          We've received your information and will contact you within 24 hours to discuss your home sale options.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form 
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Property Address *</label>
          <input
            type="text"
            name="propertyAddress"
            required
            value={formData.propertyAddress}
            onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="123 Main St, El Paso, TX"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PCS/Move Date</label>
            <select
              name="pcsDate"
              value={formData.pcsDate}
              onChange={(e) => setFormData({ ...formData, pcsDate: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="Within 2 weeks">Within 2 weeks</option>
              <option value="Within 30 days">Within 30 days</option>
              <option value="1-2 months">1-2 months</option>
              <option value="2-3 months">2-3 months</option>
              <option value="3-6 months">3-6 months</option>
              <option value="6+ months">6+ months</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Status</label>
            <select
              name="currentStatus"
              value={formData.currentStatus}
              onChange={(e) => setFormData({ ...formData, currentStatus: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select status</option>
              <option value="Living in home">Living in home</option>
              <option value="Already relocated">Already relocated</option>
              <option value="Home is vacant">Home is vacant</option>
              <option value="Renting it out">Renting it out</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Property Condition</label>
          <select
            name="propertyCondition"
            value={formData.propertyCondition}
            onChange={(e) => setFormData({ ...formData, propertyCondition: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
          >
            <option value="">Select condition</option>
            <option value="Move-in ready">Move-in ready</option>
            <option value="Needs minor repairs">Needs minor repairs</option>
            <option value="Needs major repairs">Needs major repairs</option>
            <option value="Not sure">Not sure</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Tell us about your situation, timeline, or any concerns..."
          />
          <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get My Free Cash Offer'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          We proudly support military families. Your information is secure and will never be sold.
        </p>
      </form>
    </div>
  );
};

export default function SellingHomeDuringPCSPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Selling Your El Paso Home During PCS | Military Home Sale Tips | TREX RE LLC"
        description="Expert tips, strategies, and resources for selling your El Paso home during a military PCS move. Learn your options, avoid common mistakes, and get a fast cash offer if you need to sell quickly before relocating from Fort Bliss."
        keywords="sell home during PCS, military home sale El Paso, Fort Bliss PCS selling, sell house fast military, PCS relocation El Paso TX, military move sell home"
        canonical="/elpaso-tx/selling-home-during-pcs"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Tips, Strategies and Resources for Selling an El Paso Home During PCS",
          description: "Comprehensive guide for military families selling their El Paso home during a Permanent Change of Station move.",
          author: {
            "@type": "Organization",
            name: "TREX RE LLC",
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/2e07511a3f8d03cfd8e70c0cd28f108e.jpg)`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-500/90 text-white text-sm font-semibold rounded-full mb-6">
                <i className="ri-shield-star-line mr-2"></i>
                Military Home Sale Specialists
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Selling Your El Paso Home <span className="text-teal-400">During PCS</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                PCS orders don't wait—and neither should your home sale. Get expert tips, proven strategies, and resources to sell your El Paso home on your military timeline, whether you have months or just weeks.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Close in 7-14 Days</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>No Repairs Needed</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Work Around Your Orders</span>
                </div>
              </div>
            </div>

            <div>
              <PCSContactForm 
                formId="hero-pcs-form"
                title="Get Your Free Cash Offer"
                subtitle="Sell on your PCS timeline—no stress, no hassle"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Receiving Permanent Change of Station (PCS) orders is both exciting and stressful. A new assignment means new opportunities, new experiences, and a fresh start—but it also means figuring out what to do with your current home in El Paso. For military families stationed at Fort Bliss, selling a home during a PCS move presents unique challenges that civilian homeowners rarely face: tight timelines, uncertainty about exact move dates, the stress of coordinating a cross-country relocation while maintaining military duties, and the very real possibility that you'll need to close on your home sale while already at your new duty station.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At TREX RE LLC, we've worked with hundreds of military families navigating PCS moves from Fort Bliss. We understand that your situation is different from typical home sellers. You can't simply wait for the perfect buyer to come along. You can't spend months staging your home and hosting open houses. And you certainly can't risk a deal falling through at the last minute when you're already packing the moving truck. That's why we've created this comprehensive guide—to give you the tips, strategies, and resources you need to successfully sell your El Paso home during a PCS move.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Whether you have six months until your report date or just a few weeks, this guide will help you understand your options, avoid common mistakes, and make the best decision for your family's financial future. We'll cover everything from traditional sales strategies to cash buyer options, from pricing your home correctly to handling the sale remotely after you've already relocated. Let's dive in and make your PCS transition as smooth as possible.
          </p>
        </div>

        {/* Understanding the PCS Timeline Challenge */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Understanding the PCS Timeline Challenge
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=military%20family%20looking%20at%20calendar%20and%20planning%20documents%20on%20kitchen%20table%20with%20moving%20boxes%20in%20background%20discussing%20relocation%20timeline%20warm%20natural%20lighting%20professional%20lifestyle%20photography%20showing%20stress%20and%20planning&width=800&height=500&seq=pcs-timeline-002&orientation=landscape"
            alt="Military family planning PCS timeline"
            className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The fundamental challenge of selling a home during PCS is the timeline mismatch. Traditional home sales in El Paso typically take 60-90 days from listing to closing—and that's assuming everything goes smoothly. But PCS orders don't care about real estate timelines. You might receive orders giving you 90 days to report, or you might get just 30 days. Sometimes orders change, get delayed, or accelerate unexpectedly. This uncertainty makes planning a traditional home sale extremely difficult.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Consider the typical PCS scenario: You receive orders in early spring for a summer report date. You immediately list your home, hoping to sell quickly. But the first few weeks bring only a handful of showings. Then you get an offer—but it's contingent on the buyer selling their home first. You accept anyway because time is running out. Weeks pass. The buyer's sale falls through. Now you're three weeks from your report date with no buyer and a home you can't leave behind. This scenario plays out constantly for military families, and it's why understanding your options from the start is so critical.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            The Real Cost of PCS Timing Pressure
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When you're under PCS pressure, you lose negotiating power. Buyers and their agents know you're motivated—sometimes desperately so. This can lead to accepting lower offers, agreeing to excessive repair requests, or making concessions you wouldn't otherwise consider. Some military families end up selling for 10-15% below market value simply because they ran out of time and had no other options.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The financial impact extends beyond the sale price. If you can't sell before you leave, you might face months of carrying two housing payments—your mortgage in El Paso plus rent or a new mortgage at your next duty station. Property management fees, maintenance costs, and the stress of being a long-distance landlord add up quickly. For many families, these carrying costs exceed what they would have "lost" by accepting a lower cash offer upfront. Understanding this math is essential for making smart decisions. If you're facing{' '}
            <a
              href="/avoid-foreclosure"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              foreclosure concerns
            </a>{' '}
            due to carrying costs, we can help you explore options before it's too late.
          </p>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-error-warning-line mr-2 text-amber-500"></i>
              Critical Timeline Consideration
            </h4>
            <p className="text-gray-700">
              Start planning your home sale the moment you suspect PCS orders might be coming—don't wait for official orders. Getting a head start, even by a few weeks, dramatically increases your options and reduces stress.
            </p>
          </div>
        </section>

        {/* Strategy 1: Traditional Sale with a Realtor */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Strategy 1: Traditional Sale with a Realtor
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you have 90 days or more before your report date, a traditional sale through a real estate agent typically yields the highest sale price. El Paso's real estate market has been favorable for sellers in recent years, and a skilled agent can help you maximize your return. However, traditional sales come with inherent uncertainty that can be problematic for PCS timelines.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Choosing the Right Agent for Military Sales
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Not all real estate agents understand military relocations. You need someone who has experience with PCS timelines, understands the urgency, and can market your home aggressively from day one. Look for agents with Military Relocation Professional (MRP) certification or extensive experience working with Fort Bliss families. They'll understand concepts like the Military Clause in leases, VA loan requirements, and the importance of pricing correctly from the start rather than testing the market.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A good military-experienced agent will also help you prepare for the possibility of managing the sale remotely. They'll set up systems for virtual showings, electronic signatures, and remote closing procedures. If you're looking for agent recommendations, check out our guide on{' '}
            <a
              href="/elpaso-tx/hire-realtor-el-paso"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              hiring a realtor in El Paso
            </a>
            —we've built relationships with excellent local agents who specialize in military relocations.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Pricing Strategy for PCS Sales
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When time is limited, pricing strategy becomes critical. Many sellers make the mistake of pricing high with plans to reduce later if needed. This approach is dangerous for PCS sales because you don't have "later"—every week on market is a week closer to your report date. Instead, consider pricing slightly below market value to generate immediate interest and multiple offers. A bidding war can actually push your final sale price above what you would have achieved with a higher initial asking price.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Work with your agent to analyze recent comparable sales in your neighborhood. Pay attention to days on market, not just sale prices. A home that sold for $5,000 more but took 90 days isn't a good comparable for your situation. You want to know what homes are selling for when they sell quickly. Understanding the{' '}
            <a
              href="/elpaso-tx/epcad"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              El Paso Central Appraisal District (EPCAD)
            </a>{' '}
            valuations can also help you understand your home's assessed value versus market value.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">
              Traditional Sale Pros and Cons for PCS
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-semibold text-teal-600 mb-3 flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Advantages
                </h5>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Highest potential sale price</li>
                  <li>• Professional marketing and exposure</li>
                  <li>• Agent handles negotiations</li>
                  <li>• Access to MLS and buyer pool</li>
                  <li>• Expert guidance through process</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold text-red-600 mb-3 flex items-center">
                  <i className="ri-close-line mr-2"></i>
                  Disadvantages
                </h5>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• 60-90+ day timeline typical</li>
                  <li>• Deals can fall through</li>
                  <li>• 5-6% commission costs</li>
                  <li>• Repairs often required</li>
                  <li>• Showing inconvenience</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Strategy 2: Sell to a Cash Buyer */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Strategy 2: Sell to a Cash Home Buyer
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20investor%20shaking%20hands%20with%20military%20family%20in%20front%20of%20southwestern%20style%20home%20in%20el%20paso%20texas%20showing%20successful%20cash%20home%20sale%20transaction%20warm%20lighting%20professional%20photography&width=800&height=500&seq=pcs-cash-buyer-003&orientation=landscape"
            alt="Cash buyer purchasing military family home"
            className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When PCS timelines are tight, selling to a cash buyer like TREX RE LLC offers certainty and speed that traditional sales simply cannot match. Cash buyers can close in as little as 7-14 days, work around your specific schedule, and purchase your home in any condition—no repairs, cleaning, or staging required. For many military families, the peace of mind and guaranteed closing date are worth more than the difference in sale price.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            How Cash Sales Work for PCS Moves
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The cash sale process is straightforward. You contact us with information about your property and timeline. We evaluate your home—often within 24-48 hours—and make a fair cash offer based on the property's condition and current market values. If you accept, we handle all the paperwork and coordinate with a local title company to close on your schedule. There are no financing contingencies, no appraisal requirements, and no risk of the deal falling through at the last minute.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For military families, we offer additional flexibility. Need to close before you leave but can't move out until your report date? We can work with that. Already relocated and managing the sale from your new duty station? We handle everything remotely. Have tenants in the property? We buy homes with tenants in place. Whatever your situation, we've likely seen it before and can accommodate your needs. Learn more about{' '}
            <a
              href="/elpaso-tx/cash-home-buyers-el-paso"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              how cash home buying companies work in El Paso
            </a>
            .
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Understanding Cash Offer Pricing
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Cash buyers purchase homes below full market value—typically 70-85% of what you might get through a traditional sale. This discount reflects the value we provide: speed, certainty, convenience, and the ability to buy homes in any condition. We're investors who need to make a profit after accounting for repairs, holding costs, and resale expenses.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            However, the "discount" is often smaller than it appears when you factor in the true costs of a traditional sale. Consider: 5-6% realtor commissions, 2-3% closing costs, repair and staging expenses, months of mortgage payments while waiting to sell, and the risk of price reductions if your home doesn't sell quickly. When you add these up, a cash offer that seems 15% below market value might actually net you similar proceeds—with far less stress and uncertainty.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
              When Cash Sales Make the Most Sense
            </h4>
            <ul className="space-y-2 text-gray-700">
              <li>• PCS date is less than 60 days away</li>
              <li>• Home needs repairs you can't afford or don't have time to complete</li>
              <li>• You've already relocated and need to sell remotely</li>
              <li>• Previous sale fell through and you're out of time</li>
              <li>• You value certainty over maximizing sale price</li>
              <li>• Property has{' '}
                <a href="/title-issues" className="text-teal-600 hover:text-teal-700">title issues</a>{' '}
                that complicate traditional sales
              </li>
            </ul>
          </div>
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  PCS Date Approaching Fast?
                </h2>
                <p className="text-gray-300 mb-6">
                  Don't let your home sale derail your military move. We specialize in helping Fort Bliss families sell quickly and close on their timeline—even if you've already received orders.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Cash offer within 24 hours</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Close in as little as 7 days</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>No repairs or cleaning required</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>We handle all paperwork</span>
                  </li>
                </ul>
              </div>
              <div>
                <PCSContactForm 
                  formId="middle-pcs-form"
                  title="Get Your Cash Offer Today"
                  subtitle="No obligation, no pressure"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Strategy 3: Keep as Rental */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Strategy 3: Keep Your Home as a Rental Property
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Some military families choose to keep their El Paso home as a rental investment rather than selling during PCS. With Fort Bliss creating constant demand for rental housing, this can be a viable wealth-building strategy. However, being a long-distance landlord comes with significant challenges and responsibilities that you should carefully consider before choosing this path.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            The Case for Keeping Your Home
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            El Paso's rental market is strong, particularly in areas near Fort Bliss. Your BAH-level mortgage payment often translates to positive cash flow when rented to another military family or civilian tenant. Over time, you build equity while someone else pays your mortgage. If you plan to return to Fort Bliss for a future assignment, keeping the home gives you a place to come back to. And real estate has historically been a solid long-term investment.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            The Challenges of Long-Distance Landlording
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The reality of being a landlord from across the country is often more difficult than anticipated. You'll need a property manager (typically 8-10% of monthly rent), and even good managers can't prevent all problems. Vacancies happen—sometimes at the worst possible times. Maintenance issues arise that require quick decisions and significant expenses. Problem tenants can damage your property or stop paying rent, leading to costly eviction processes.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Many military families start as landlords with good intentions but eventually decide to sell when the hassle outweighs the benefits. If you're currently renting out your El Paso property and ready to sell, we buy{' '}
            <a
              href="/rental-property"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              rental properties with tenants in place
            </a>
            —you don't need to wait for leases to expire or deal with the complications of selling an occupied property.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">
              Questions to Ask Before Becoming a Landlord
            </h4>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-question-line text-teal-600 mr-3 mt-1"></i>
                <span>Can you afford the mortgage if the property sits vacant for 2-3 months?</span>
              </li>
              <li className="flex items-start">
                <i className="ri-question-line text-teal-600 mr-3 mt-1"></i>
                <span>Do you have reserves for major repairs (roof, HVAC, plumbing)?</span>
              </li>
              <li className="flex items-start">
                <i className="ri-question-line text-teal-600 mr-3 mt-1"></i>
                <span>Are you comfortable making decisions about your property from thousands of miles away?</span>
              </li>
              <li className="flex items-start">
                <i className="ri-question-line text-teal-600 mr-3 mt-1"></i>
                <span>Have you researched property management companies in El Paso?</span>
              </li>
              <li className="flex items-start">
                <i className="ri-question-line text-teal-600 mr-3 mt-1"></i>
                <span>Do you understand the tax implications of rental income?</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Preparing Your Home for Sale */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Preparing Your Home for a Quick Sale
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=beautifully%20staged%20modern%20living%20room%20interior%20in%20southwestern%20style%20home%20ready%20for%20sale%20with%20neutral%20decor%20clean%20organized%20space%20natural%20lighting%20through%20large%20windows%20professional%20real%20estate%20photography&width=800&height=500&seq=pcs-staged-home-004&orientation=landscape"
            alt="Staged home ready for sale"
            className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you're pursuing a traditional sale, preparing your home properly can significantly reduce time on market. First impressions matter enormously in real estate, and buyers make decisions within seconds of walking through the door. Here are the most impactful preparations for a quick sale:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Declutter and Depersonalize
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Military families often accumulate items from multiple duty stations. Before listing, ruthlessly declutter every room. Remove personal photos, military memorabilia, and anything that makes the space feel like "your" home rather than a blank canvas for buyers. Consider this a head start on packing—donate, sell, or store items you won't need immediately. A decluttered home photographs better, shows better, and sells faster.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Focus on High-Impact Repairs
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            You don't have time for major renovations, but addressing obvious issues prevents buyer objections. Fix leaky faucets, replace burned-out light bulbs, touch up paint scuffs, and ensure all doors and windows operate properly. These small fixes cost little but eliminate red flags that make buyers nervous. If your home has more significant issues, a cash sale might be the better option—we buy homes in any condition and handle repairs ourselves.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Curb Appeal Matters
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            El Paso's desert landscaping is low-maintenance, but that doesn't mean you can neglect the exterior. Trim bushes, remove weeds, add fresh mulch to flower beds, and ensure your front door and entry area look welcoming. Power wash driveways and walkways if needed. A well-maintained exterior signals to buyers that the home has been cared for throughout.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-lightbulb-line mr-2 text-teal-600"></i>
              Quick Wins for Faster Sales
            </h4>
            <div className="grid md:grid-cols-2 gap-4 text-gray-700">
              <ul className="space-y-2">
                <li>• Deep clean entire home</li>
                <li>• Steam clean carpets</li>
                <li>• Clean windows inside and out</li>
                <li>• Organize closets and storage</li>
              </ul>
              <ul className="space-y-2">
                <li>• Fresh neutral paint if needed</li>
                <li>• Update dated light fixtures</li>
                <li>• Replace worn cabinet hardware</li>
                <li>• Professional photography</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Managing the Sale Remotely */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Managing Your Home Sale from Your New Duty Station
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=military%20service%20member%20in%20uniform%20using%20laptop%20computer%20for%20video%20call%20about%20real%20estate%20transaction%20in%20modern%20office%20setting%20with%20documents%20and%20paperwork%20visible%20professional%20business%20photography&width=800&height=500&seq=pcs-remote-sale-005&orientation=landscape"
            alt="Managing home sale remotely"
            className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Sometimes, despite your best efforts, you'll need to manage your home sale after you've already relocated. This is increasingly common for military families and, while challenging, is entirely manageable with the right preparation and team in place.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Setting Up for Remote Success
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Before you leave El Paso, establish systems for remote management. Ensure your agent has a lockbox and can show the property without you present. Set up electronic signature capabilities through DocuSign or similar platforms. Provide your agent with a power of attorney for real estate transactions if needed—this allows them to sign certain documents on your behalf. Confirm that your title company can handle remote closings, which have become standard since the pandemic.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Communication is Critical
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Establish clear communication expectations with your agent. How often will they update you? What decisions require your input versus what can they handle independently? Time zone differences can complicate communication, so agree on the best methods and times to connect. Many military families find that a weekly video call plus text updates for urgent matters works well.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            The Cash Buyer Advantage for Remote Sales
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you've already relocated and your home hasn't sold, a cash buyer can simplify the process dramatically. We handle everything remotely—you don't need to return to El Paso for showings, inspections, or closing. One phone call, one offer, one closing date. For military families juggling a new assignment while trying to sell a home across the country, this simplicity is invaluable. We've closed deals with service members stationed overseas, deployed, or simply too busy with their new duties to manage a traditional sale.
          </p>
        </section>

        {/* Financial Considerations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Financial Considerations for PCS Home Sales
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Understanding the financial implications of your home sale decision is crucial. Military families often focus solely on sale price without considering the full picture of costs, timing, and opportunity costs. Let's break down the real numbers.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            True Cost Comparison: Traditional vs. Cash Sale
          </h3>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-4 text-left">Cost Factor</th>
                  <th className="p-4 text-left">Traditional Sale</th>
                  <th className="p-4 text-left">Cash Sale</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b">
                  <td className="p-4 font-medium">Sale Price (Example: $250K home)</td>
                  <td className="p-4">$250,000</td>
                  <td className="p-4">$212,500 (85%)</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Realtor Commission (6%)</td>
                  <td className="p-4">-$15,000</td>
                  <td className="p-4">$0</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Closing Costs (2-3%)</td>
                  <td className="p-4">-$6,250</td>
                  <td className="p-4">$0 (we pay)</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Repairs/Staging</td>
                  <td className="p-4">-$5,000 (estimate)</td>
                  <td className="p-4">$0</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Carrying Costs (3 months)</td>
                  <td className="p-4">-$6,000</td>
                  <td className="p-4">$0</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Price Reduction Risk</td>
                  <td className="p-4">-$5,000 (potential)</td>
                  <td className="p-4">$0</td>
                </tr>
                <tr className="bg-teal-50 font-bold">
                  <td className="p-4">Net Proceeds</td>
                  <td className="p-4">$212,750</td>
                  <td className="p-4">$212,500</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            As this example shows, the gap between traditional and cash sales is often much smaller than the headline numbers suggest. And this doesn't account for the stress, uncertainty, and time investment of a traditional sale—factors that are especially significant during a PCS move.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Tax Implications to Consider
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Military families receive special tax benefits when selling a primary residence. The Servicemembers Civil Relief Act (SCRA) provides protections, and the Military Family Tax Relief Act allows you to suspend the 5-year ownership requirement for capital gains exclusion during qualifying military service. This means you may still qualify for the $250,000 (single) or $500,000 (married) capital gains exclusion even if you haven't lived in the home for the required period. Consult a tax professional familiar with military benefits to understand your specific situation.
          </p>
        </section>

        {/* Common Mistakes to Avoid */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Common Mistakes Military Families Make When Selling During PCS
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            After working with hundreds of military families, we've seen the same mistakes repeated. Avoiding these pitfalls can save you thousands of dollars and significant stress:
          </p>

          <div className="space-y-6 mb-8">
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Mistake #1: Waiting Too Long to Start
              </h4>
              <p className="text-gray-700">
                Many families wait for official orders before taking action. By then, you've lost valuable time. Start preparing your home and exploring options as soon as you suspect a PCS might be coming.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Mistake #2: Overpricing to "Test the Market"
              </h4>
              <p className="text-gray-700">
                You don't have time to test the market. Overpricing leads to stale listings, price reductions, and ultimately selling for less than if you'd priced correctly from the start.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Mistake #3: Accepting Contingent Offers
              </h4>
              <p className="text-gray-700">
                Offers contingent on the buyer selling their home are risky for PCS timelines. If their sale falls through, you're back to square one with less time than before.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Mistake #4: Not Having a Backup Plan
              </h4>
              <p className="text-gray-700">
                Hope is not a strategy. Know your options if your traditional sale doesn't work out. Having a cash buyer offer in your back pocket provides peace of mind and negotiating leverage.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Mistake #5: Underestimating Carrying Costs
              </h4>
              <p className="text-gray-700">
                Mortgage, insurance, HOA, utilities, maintenance—these costs add up quickly when you're paying for two homes. Factor these into your decision-making.
              </p>
            </div>
          </div>
        </section>

        {/* Resources Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Resources for Military Families Selling in El Paso
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=helpful%20resources%20and%20documents%20spread%20on%20desk%20including%20military%20relocation%20guide%20real%20estate%20paperwork%20and%20laptop%20showing%20el%20paso%20texas%20map%20warm%20office%20lighting%20professional%20photography&width=800&height=500&seq=pcs-resources-006&orientation=landscape"
            alt="Military relocation resources"
            className="w-full h-96 object-cover object-top rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Fort Bliss and the El Paso community offer numerous resources to help military families with PCS transitions. Take advantage of these services:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-building-2-line text-teal-600 mr-2"></i>
                Army Community Service (ACS)
              </h4>
              <p className="text-gray-700 text-sm mb-3">
                Offers relocation assistance, financial counseling, and connections to local resources. Their Relocation Readiness Program can help with PCS planning.
              </p>
              <p className="text-gray-600 text-sm">Location: Building 505, Fort Bliss</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-home-heart-line text-teal-600 mr-2"></i>
                Housing Services Office
              </h4>
              <p className="text-gray-700 text-sm mb-3">
                Provides information about on-post housing, off-post rentals, and can connect you with vetted real estate professionals.
              </p>
              <p className="text-gray-600 text-sm">Location: Building 624, Fort Bliss</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-bank-line text-teal-600 mr-2"></i>
                Personal Financial Counseling
              </h4>
              <p className="text-gray-700 text-sm mb-3">
                Free financial counseling to help you understand the financial implications of selling, renting, or keeping your home.
              </p>
              <p className="text-gray-600 text-sm">Available through ACS and Military OneSource</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                <i className="ri-scales-3-line text-teal-600 mr-2"></i>
                Legal Assistance Office
              </h4>
              <p className="text-gray-700 text-sm mb-3">
                Can review real estate contracts, explain your rights under SCRA, and provide guidance on powers of attorney for remote closings.
              </p>
              <p className="text-gray-600 text-sm">Location: Building 1841, Fort Bliss</p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For more information about the El Paso area and Fort Bliss, explore our comprehensive{' '}
            <a
              href="/elpaso-tx/fort-bliss-military-relocation"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              Fort Bliss Military Relocation Guide
            </a>{' '}
            and stay updated with{' '}
            <a
              href="/local-news-el-paso-texas"
              className="text-teal-600 hover:text-teal-700 font-medium"
            >
              local El Paso news and real estate updates
            </a>
            .
          </p>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How quickly can you close on my home before I PCS?
              </h3>
              <p className="text-gray-700">
                We can close in as little as 7 days, though 14-21 days is more typical. We work around your PCS schedule and can coordinate closing even if you've already relocated. We've successfully closed with service members who were already at their new duty station or even deployed overseas.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if my home needs repairs I can't afford to make?
              </h3>
              <p className="text-gray-700">
                We buy homes in any condition—no repairs, cleaning, or updates required. Whether your home needs minor cosmetic work or major structural repairs, we'll make a fair offer based on its current condition. This is one of the biggest advantages of selling to a cash buyer during PCS.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I get a cash offer even if I'm already working with a realtor?
              </h3>
              <p className="text-gray-700">
                Yes, but check your listing agreement first. Most agreements allow you to cancel with notice, and some have exceptions for direct sales to investors. We're happy to provide a backup offer that you can use if your traditional sale doesn't work out before your PCS date.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if I owe more on my mortgage than the home is worth?
              </h3>
              <p className="text-gray-700">
                This situation, called being "underwater," complicates any sale. Options include negotiating a short sale with your lender, bringing cash to closing to cover the difference, or exploring loan modification programs. Contact us to discuss your specific situation—we may still be able to help.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Do you buy homes with tenants currently living in them?
              </h3>
              <p className="text-gray-700">
                Yes, we purchase properties with tenants in place. You don't need to wait for leases to expire or go through the eviction process. We'll handle the tenant situation after closing, making this a stress-free option for military landlords who need to sell.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Is there any cost or obligation to get a cash offer?
              </h3>
              <p className="text-gray-700">
                Absolutely not. Getting a cash offer from us is completely free with no obligation. We'll evaluate your property, make a fair offer, and you decide if it works for your situation. Many military families get our offer as a backup plan while pursuing a traditional sale—there's no pressure either way.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What areas around Fort Bliss do you buy in?
              </h3>
              <p className="text-gray-700">
                We buy properties throughout El Paso County and surrounding areas, including Northeast El Paso, Horizon City, the East Side, West Side, Upper Valley, and all neighborhoods popular with military families. We also purchase properties in the New Mexico areas near Fort Bliss.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-shield-star-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Thank You for Your Service
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                We're honored to help military families navigate PCS home sales. Whether you need to sell in 7 days or 7 months, we're here to provide options, guidance, and support throughout your transition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <PCSContactForm 
                formId="final-pcs-form"
                title="Get Your Free Cash Offer Today"
                subtitle="No obligation—just honest answers and real options"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">
                Prefer to talk? Call us directly:
              </p>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line mr-2"></i>
                (915) 206-5814
              </a>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Related Resources for Military Families
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/elpaso-tx/fort-bliss-military-relocation"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-map-pin-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Fort Bliss Relocation Guide</p>
                <p className="text-sm text-gray-600">Complete guide for PCSing to/from Fort Bliss</p>
              </div>
            </a>
            <a
              href="/inherited-homes"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-heart-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Inherited Homes</p>
                <p className="text-sm text-gray-600">Selling property received through inheritance</p>
              </div>
            </a>
            <a
              href="/elpaso-tx/texas-probate-guide"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-file-text-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Texas Probate Guide</p>
                <p className="text-sm text-gray-600">Understanding the probate process</p>
              </div>
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Get Cash Offer</p>
                <p className="text-sm text-gray-600">Request your no-obligation cash offer</p>
              </div>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
